import { useState, useEffect } from "react";
import BackMethods from "../../back_component/BackMethods";
import { useNavigate, useLocation } from "react-router-dom";
import { PATHS } from "../../routes/paths";
import Activity from "../../models/Activity";
import { can, PERMISSIONS } from "../../auth/permissions";
import "../../styles/admin-shared.css";
import "../Users/users-admin.css";
import {
    FaPlus, FaEdit, FaTrash, FaBuilding,
    FaArrowRight
} from "react-icons/fa";
import ActivitiesManagementModals from "./ActivitiesManagementModals";

export default function ActivitiesManagementPage() {
    const location = useLocation();
    const initialSuccessMessage = location.state?.successMessage;
    const navigate = useNavigate();
    const { backEnd_request } = BackMethods();

    const [activities, setActivities] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isLoading, setIsLoading] = useState(true);

    const [successMessage, setSuccessMessage] = useState(initialSuccessMessage || "");
    const [deleteSuccessMessage, setDeleteSuccessMessage] = useState("");

    const [modal, setModal] = useState(null);
    const [modalActivityId, setModalActivityId] = useState(null);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);

    const canCreate = can(PERMISSIONS.ACTIVITIES_CREATE);
    const canEdit = can(PERMISSIONS.ACTIVITIES_EDIT);
    const canDelete = can(PERMISSIONS.ACTIVITIES_DELETE);
    const hasAnyAction = true;

    const fetchActivities = async () => {
        setIsLoading(true);
        try {
            const res = await backEnd_request.get("/Activities", {
                params: {
                    q: searchTerm,
                    page: currentPage,
                    size: pageSize
                }
            });

            const { total, data } = res.data;
            setActivities(data.map(a => new Activity(a)));
            setTotal(total);
            if (total) {
                setTotalPages(Math.ceil(total / pageSize));
            }
        } catch (error) {
            console.error("Fetch Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchActivities();
    }, [currentPage, searchTerm]);

    useEffect(() => {
        setCurrentPage(1);
        fetchActivities();
    }, [pageSize]);

    const handlePageChange = (p) => {
        if (p < 1 || p > totalPages) return;
        setCurrentPage(p);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handlePageSizeChange = (newSize) => {
        setPageSize(parseInt(newSize));
    };

    const handleDelete = async (activityId) => {
        if (window.confirm("هل أنت متأكد من حذف هذا النشاط؟")) {
            try {
                await backEnd_request.delete(`/Activities/${activityId}`);
                fetchActivities();
                setDeleteSuccessMessage("تم حذف النشاط بنجاح ✅");
                setTimeout(() => setDeleteSuccessMessage(""), 3000);
            } catch (error) {
                console.log(error.response?.data || error);
            }
        }
    };

    const handleNewActivitySuccess = ({ message } = {}) => {
        const msg = message || "تم حفظ التغييرات بنجاح";
        setSuccessMessage(msg);
        setTimeout(() => setSuccessMessage(""), 3000);
        fetchActivities();
    };

    const openCreate = () => {
        setModal("create");
        setModalActivityId(null);
    };

    const openEdit = (activityId) => {
        setModal("edit");
        setModalActivityId(activityId);
    };

    const closeModal = () => {
        setModal(null);
        setModalActivityId(null);
    };

    return (
        <div className="users-page-minimal admin-page" style={{ direction: "rtl", fontFamily: "Cairo, sans-serif" }}>
            <div className="container pb-5 pt-4 px-3">

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom gap-3">
                    <div>
                        <h3 className="fw-bold text-primary mb-1 d-flex align-items-center gap-2">
                            <span className="rounded-3 bg-primary bg-opacity-10 p-2 d-inline-flex text-primary">
                                <FaBuilding />
                            </span>
                            إدارة النشاطات
                        </h3>
                        <p className="text-muted small mb-0">
                            إدارة جميع أنواع النشاطات في التطبيق والتحكم بها.
                        </p>
                    </div>
                    <div className="d-flex gap-2">
                        {canCreate && (
                            <button
                                onClick={openCreate}
                                className="btn btn-primary d-flex align-items-center px-4 rounded-pill shadow-sm"
                            >
                                <FaPlus className="ms-2" /> إضافة نشاط
                            </button>
                        )}
                        <button onClick={() => navigate(PATHS.ADMIN.HOME)} className="btn btn-outline-secondary rounded-pill">
                            <FaArrowRight className="ms-1" /> رجوع
                        </button>
                    </div>
                </div>

                <div className="users-toolbar mb-3">
                    <div className="users-count-chip">
                        إجمالي النشاطات
                        <span>{total}</span>
                    </div>
                    <div className="users-search-wrap">
                        <div className="bg-white border rounded-4 p-2 shadow-sm">
                            <div className="input-group border-0">
                                <span className="input-group-text bg-transparent border-0 text-muted"><i className="bi bi-search"></i></span>
                                <input
                                    type="text"
                                    className="form-control border-0 shadow-none py-2"
                                    placeholder="ابحث باسم النشاط..."
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {(successMessage || deleteSuccessMessage) && (
                    <div className="mb-3">
                        {successMessage && (
                            <div className="users-admin-alert px-3 py-2 d-flex align-items-center justify-content-between gap-2">
                                <span>{successMessage}</span>
                            </div>
                        )}
                        {deleteSuccessMessage && (
                            <div className="users-admin-alert px-3 py-2 d-flex align-items-center justify-content-between gap-2">
                                <span>{deleteSuccessMessage}</span>
                            </div>
                        )}
                    </div>
                )}

                {isLoading ? (
                    <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-secondary"></div></div>
                ) : (
                    <div className="users-table-shell activities-table-shell">
                        <div className="table-responsive">
                            <table className="table users-table align-middle mb-0 users-table--activities">
                                <thead>
                                    <tr>
                                        <th className="text-center col-index">#</th>
                                        <th className="text-end">اسم النشاط</th>
                                        <th className="text-center">عدد المنشآت</th>
                                        <th className="text-center">الحالة</th>
                                        {hasAnyAction && <th className="text-center">الإجراءات</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {activities.map((act, index) => (
                                        <tr key={act.activityId}>
                                            <td className="text-center col-index fw-semibold text-muted" data-label="#">
                                                {(currentPage - 1) * pageSize + index + 1}
                                            </td>
                                            <td className="text-end" data-label="اسم النشاط">
                                                <span className="user-name">{act.activityType}</span>
                                            </td>
                                            <td className="text-center" data-label="عدد المنشآت">
                                                <span className="role-pill">
                                                    {act.establishmentsCount || 0}
                                                </span>
                                            </td>
                                            <td className="text-center" data-label="الحالة">
                                                {act.isActive ? (
                                                    <span className="status-pill status-active">نشط</span>
                                                ) : (
                                                    <span className="status-pill status-inactive">غير نشط</span>
                                                )}
                                            </td>
                                            {hasAnyAction && (
                                                <td className="text-center" data-label="الإجراءات">
                                                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                                                        <button
                                                            onClick={() => navigate(`/admin/establishments-list?activityType=${encodeURIComponent(act.activityType)}`)}
                                                            className="btn-minimal"
                                                            title="عرض المنشآت"
                                                        >
                                                            <FaBuilding />
                                                        </button>
                                                        {canEdit && (
                                                            <button
                                                                onClick={() => openEdit(act.activityId)}
                                                                className="btn-minimal"
                                                                title="تعديل"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() => handleDelete(act.activityId)}
                                                                className="btn-minimal text-danger-hover"
                                                                title="حذف"
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        {activities.length === 0 && (
                            <div className="empty-state">
                                <FaBuilding className="text-muted mb-3" size={48} />
                                <h5 className="text-muted">لا توجد نشاطات</h5>
                                <p className="text-muted small">جرب تغيير عبارة البحث أو أضف نشاطًا جديدًا</p>
                            </div>
                        )}
                    </div>
                )}

                {activities.length > 0 && (
                    <div className="admin-table-footer d-flex flex-wrap justify-content-between align-items-stretch align-items-md-center gap-3 mt-4">
                        <div className="admin-page-size">
                            <span className="admin-page-size__icon" aria-hidden>
                                <i className="bi bi-layout-three-columns" />
                            </span>
                            <label htmlFor="activities-page-size" className="admin-page-size__label">عناصر الصفحة</label>
                            <div className="admin-page-size__select-wrap">
                                <select
                                    id="activities-page-size"
                                    className="admin-page-size__select"
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(e.target.value)}
                                >
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={30}>30</option>
                                </select>
                            </div>
                        </div>

                        <div className="admin-pagination">
                            <span className="admin-pagination__summary">
                                صفحة <strong>{currentPage}</strong> من <strong>{totalPages}</strong>
                            </span>
                            <div className="minimal-pagination admin-pagination__bar">
                                <button
                                    type="button"
                                    className="page-arrow"
                                    disabled={currentPage === 1}
                                    onClick={() => handlePageChange(currentPage - 1)}
                                >
                                    <i className="bi bi-chevron-right" />
                                </button>
                                <div className="page-numbers">
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                        <button
                                            key={p}
                                            type="button"
                                            className={`page-dot ${currentPage === p ? "active" : ""}`}
                                            onClick={() => handlePageChange(p)}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    className="page-arrow"
                                    disabled={currentPage === totalPages}
                                    onClick={() => handlePageChange(currentPage + 1)}
                                >
                                    <i className="bi bi-chevron-left" />
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <ActivitiesManagementModals
                    modal={modal}
                    activityId={modalActivityId}
                    onClose={closeModal}
                    onSaved={handleNewActivitySuccess}
                    canCreate={canCreate}
                    canEdit={canEdit}
                />
            </div>
        </div>
    );
}
